import React, { useState } from 'react';
import PageBanner from '../components/PageBanner';
import CTASection from '../components/CTASection';
import { Calendar, MapPin, Users, Play, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import EventsTimeline from '../components/EventsTimeline';

const WHATSAPP_CHANNEL = 'https://whatsapp.com/channel/0029VbAwgy47dmeeh3vv2N0t';

const EVENT_VIDEOS = [
  {
    title: 'MMF Community Event Highlights',
    description:
      'A behind-the-scenes look at one of our recent community events—celebrating the girls, mentors, and partners who make our mission possible. Watch the energy, the joy, and the impact in action.',
    src: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/igz5igx1_Event%20video.mp4',
    poster:
      'https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=900&q=80',
    date: 'Recent Event',
    location: 'Johannesburg, South Africa',
  },
  {
    title: 'A Moment with President Cyril Ramaphosa',
    description:
      'A special moment captured at a national gathering—reflecting on the urgent need for educational equity and the role of foundations like MMF in shaping a brighter future for African girls.',
    src: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/1cgg1n9s_Ramaphosa.mp4',
    poster:
      'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=900&q=80',
    date: 'National Event',
    location: 'South Africa',
  },
];

const UPCOMING = [
  {
    title: 'Girls Empowerment Summit',
    date: 'Saturday, 14 March 2026',
    location: 'Sandton Convention Centre, Johannesburg',
    attendees: '500+ expected',
    tag: 'Summit',
  },
  {
    title: 'Mobile Library Launch — Soweto',
    date: 'Saturday, 28 March 2026',
    location: 'Diepkloof Community Hall, Soweto',
    attendees: '200+ expected',
    tag: 'Community',
  },
  {
    title: 'International Boy Child Day',
    date: 'Wednesday, 11 May 2026',
    location: 'Online & multiple SA cities',
    attendees: 'Open to all',
    tag: 'Awareness',
  },
];

const VideoCard = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <article className="bg-white rounded-sm overflow-hidden shadow-sm card-lift border border-slate-100">
      <div className="relative aspect-video bg-slate-900">
        {playing ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={video.src}
            controls
            autoPlay
            playsInline
            poster={video.poster}
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full group"
            aria-label={`Play ${video.title}`}
          >
            <img
              src={video.poster}
              alt={video.title}
              className="w-full h-full object-cover"
            />
            <span className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
            <span className="absolute inset-0 grid place-items-center">
              <span className="w-20 h-20 rounded-full bg-[#F97559] grid place-items-center text-white shadow-2xl transition-transform group-hover:scale-110">
                <Play size={32} fill="white" />
              </span>
            </span>
            <span className="absolute top-4 left-4 bg-[#2BBECF] text-white text-xs font-semibold px-3 py-1.5 rounded-sm">
              {video.date}
            </span>
          </button>
        )}
      </div>
      <div className="p-7">
        <h3 className="heading-display text-xl md:text-2xl font-bold text-slate-900">
          {video.title}
        </h3>
        <p className="mt-3 text-slate-600 text-[14.5px] leading-7">
          {video.description}
        </p>
        <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
          <MapPin size={14} className="text-[#F97559]" />
          <span>{video.location}</span>
        </div>
      </div>
    </article>
  );
};

const Events = () => (
  <main>
    <PageBanner title="Events & Highlights" crumb="Events" />

    {/* Horizontal events timeline */}
    <EventsTimeline />

    {/* Video highlights */}
    <section className="py-20 md:py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-12">
          <p className="uppercase text-sm tracking-[0.25em] text-slate-500 mb-3">
            Recent Highlights
          </p>
          <h2 className="heading-display text-4xl md:text-5xl font-bold text-slate-900">
            Watch Our Story
          </h2>
          <div className="mx-auto mt-4 flex items-center justify-center gap-1">
            <span className="w-8 h-0.5 bg-[#F97559]" />
            <span className="w-2 h-2 rounded-full bg-[#F97559]" />
            <span className="w-8 h-0.5 bg-[#F97559]" />
          </div>
          <p className="mt-6 max-w-2xl mx-auto text-slate-600 leading-7 text-[15px]">
            Get an inside look at MMF events, community gatherings, and moments
            of national significance that shape our journey.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {EVENT_VIDEOS.map((v, i) => (
            <VideoCard key={i} video={v} />
          ))}
        </div>
      </div>
    </section>

    {/* WhatsApp Channel CTA */}
    <section className="py-16 md:py-20 bg-[#0e1729] text-white">
      <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#25D366]/15 text-[#25D366] px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
            <MessageCircle size={14} /> WhatsApp Channel
          </div>
          <h2 className="heading-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white">
            Join our WhatsApp Channel <br />
            <span className="text-[#25D366]">for live updates</span>
          </h2>
          <p className="mt-5 text-slate-300 leading-8 text-[15px] max-w-xl">
            Get instant updates on upcoming events, project milestones,
            scholarship announcements and powerful stories of impact—delivered
            straight to your WhatsApp.
          </p>
          <a
            href={WHATSAPP_CHANNEL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white px-7 py-4 rounded-sm font-semibold transition-colors shadow-lg hover:shadow-xl hover:-translate-y-0.5 duration-300"
          >
            <MessageCircle size={18} /> Follow MMF on WhatsApp
          </a>
        </div>
        <div className="relative">
          <div className="bg-white/5 border border-white/10 rounded-sm p-8 backdrop-blur">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-[#25D366] grid place-items-center text-white">
                <MessageCircle size={24} />
              </div>
              <div>
                <p className="heading-display font-bold text-lg text-white">
                  Moleboheng Matli Foundation
                </p>
                <p className="text-xs text-slate-400">Official Channel · WhatsApp</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              {[
                'Event invitations & RSVPs',
                'New scholarship announcements',
                'Volunteer opportunities near you',
                'Stories from beneficiaries',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#25D366] text-white grid place-items-center mt-0.5 shrink-0 text-[10px] font-bold">
                    ✓
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <a
              href={WHATSAPP_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 block text-center bg-white/10 hover:bg-white/15 text-white text-sm font-semibold py-3 rounded-sm transition-colors break-all"
            >
              whatsapp.com/channel/MMF
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* Upcoming events section removed — replaced by the horizontal EventsTimeline above */}

    <CTASection />
  </main>
);

export default Events;
